import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/**
 * ANC Preventive Treatment Definitions — Uganda MOH Guidelines
 *
 * Each intervention: key, label, icon, category, totalDoses, dueFromWeek, dueToWeek, notes
 */
const INTERVENTIONS = [
    {
        key: "td_vaccine", label: "Td Vaccine", icon: "💉", category: "vaccination",
        totalDoses: 5, doseLabels: ["Td1", "Td2", "Td3", "Td4", "Td5"],
        dueFromWeek: 0, dueToWeek: 40,
        notes: "5-dose schedule. Td1 at first contact, Td2 at 4+ weeks after Td1, then yearly.",
    },
    {
        key: "ipt_sp", label: "IPTp-SP (Fansidar)", icon: "💊", category: "malaria",
        totalDoses: 3, doseLabels: ["IPTp1", "IPTp2", "IPTp3+"],
        dueFromWeek: 13, dueToWeek: 40,
        notes: "Start after quickening (>13 weeks). Give at each ANC contact, ≥1 month apart.",
    },
    {
        key: "iron_folic_acid", label: "Iron/Folic Acid", icon: "🟤", category: "nutrition",
        totalDoses: 8, doseLabels: ["Contact 1","Contact 2","Contact 3","Contact 4","Contact 5","Contact 6","Contact 7","Contact 8"],
        dueFromWeek: 0, dueToWeek: 40,
        notes: "Daily supplementation throughout pregnancy. 30-count pills dispensed per visit.",
        hasQuantity: true,
        hasPharmacy: true,  // dispensed via pharmacy — prescription_id linkable
    },
    {
        key: "mebendazole", label: "Mebendazole (Deworming)", icon: "🪱", category: "deworming",
        totalDoses: 1, doseLabels: ["Dose 1"],
        dueFromWeek: 13, dueToWeek: 40,
        notes: "Single dose after first trimester (>13 weeks). 500mg stat or 100mg BD x 3 days.",
    },
    {
        key: "llin", label: "LLIN (Mosquito Net)", icon: "🛏", category: "malaria",
        totalDoses: 1, doseLabels: ["Provided"],
        dueFromWeek: 0, dueToWeek: 40,
        notes: "Long-lasting insecticidal net. Provide at first ANC contact.",
    },
    {
        key: "ctx_prophylaxis", label: "CTX Prophylaxis (HIV+)", icon: "💊", category: "hiv",
        totalDoses: 8, doseLabels: ["C1","C2","C3","C4","C5","C6","C7","C8"],
        dueFromWeek: 0, dueToWeek: 40,
        notes: "Cotrimoxazole prophylaxis for HIV+ mothers. Daily throughout pregnancy.",
        conditional: true,
    },
    {
        key: "art", label: "ART (HIV+)", icon: "🏥", category: "hiv",
        totalDoses: 8, doseLabels: ["C1","C2","C3","C4","C5","C6","C7","C8"],
        dueFromWeek: 0, dueToWeek: 40,
        notes: "Antiretroviral therapy for HIV+ mothers. Confirm adherence at each contact.",
        conditional: true,
    },
    {
        key: "calcium", label: "Calcium Supplementation", icon: "🦴", category: "nutrition",
        totalDoses: 8, doseLabels: ["C1","C2","C3","C4","C5","C6","C7","C8"],
        dueFromWeek: 0, dueToWeek: 40,
        notes: "1.5-2g elemental calcium daily, especially where dietary calcium is low.",
        hasQuantity: true,
        hasPharmacy: true,  // dispensed via pharmacy — prescription_id linkable
    },
    {
        key: "aspirin", label: "Low-dose Aspirin", icon: "⚪", category: "preeclampsia",
        totalDoses: 1, doseLabels: ["Started"],
        dueFromWeek: 12, dueToWeek: 36,
        notes: "75mg daily from 12 weeks to 36 weeks for preeclampsia prevention in high-risk women.",
        conditional: true,
        hasPharmacy: true,  // dispensed via pharmacy — prescription_id linkable
    },
];

/**
 * useANCInterventions — Custom hook for Preventive Treatments Tracker
 */
export const useANCInterventions = (actions, patient_number, visit_id) => {

    const [bookingData, setBookingData] = useState(null);
    const [loadingBooking, setLoadingBooking] = useState(true);
    const [interventions, setInterventions] = useState([]);
    const [loadingInterventions, setLoadingInterventions] = useState(false);
    const [recordDialog, setRecordDialog] = useState({ open: false, intervention: null, doseNumber: null });
    const [formData, setFormData] = useState({
        date_given: new Date().toISOString().slice(0, 10),
        quantity: "",
        prescription_id: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [showConditional, setShowConditional] = useState(false);

    /* ─── Fetch booking ─── */
    useEffect(() => {
        if (!patient_number) return;
        setLoadingBooking(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/booking/patient/" + patient_number)
            .then((res) => {
                const data = res.data || {};
                if (data.id) setBookingData(data);
            })
            .catch(() => { setBookingData(null); })
            .finally(() => { setLoadingBooking(false); });
    }, [patient_number]);

    /* ─── Fetch interventions ─── */
    useEffect(() => {
        if (!bookingData || !bookingData.id) return;
        setLoadingInterventions(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/interventions/" + bookingData.id)
            .then((res) => {
                const data = res.data || {};
                // Backend returns { booking_id, interventions: [...] }
                const list = Array.isArray(data) ? data : (data.interventions || []);
                setInterventions(list);
            })
            .catch(() => { setInterventions([]); })
            .finally(() => { setLoadingInterventions(false); });
    }, [bookingData]);

    /* ─── Current GA ─── */
    const gestationalAge = useMemo(() => {
        if (!bookingData || !bookingData.lnmp) return null;
        const lnmpDate = new Date(bookingData.lnmp);
        const todayDate = new Date();
        const diffMs = todayDate - lnmpDate;
        if (diffMs < 0) return null;
        return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
    }, [bookingData]);

    /* ─── Matrix data: merge definitions with actual interventions ─── */
    const matrixData = useMemo(() => {
        return INTERVENTIONS.map((def) => {
            const givenDoses = interventions.filter((iv) => iv.intervention_type === def.key);

            const doses = [];
            for (let i = 0; i < def.totalDoses; i++) {
                const doseNum = i + 1;
                const given = givenDoses.find((g) => (g.dose_number || 1) === doseNum);
                let isDue = false;
                if (gestationalAge !== null && !given) {
                    isDue = gestationalAge >= def.dueFromWeek && gestationalAge <= def.dueToWeek;
                    // For multi-dose, only the next unrecorded dose is due
                    if (def.totalDoses > 1 && doseNum > givenDoses.length + 1) {
                        isDue = false;
                    }
                }

                doses.push({
                    doseNumber: doseNum,
                    label: def.doseLabels[i] || "Dose " + doseNum,
                    given: !!given,
                    dateGiven: given ? given.date_given : null,
                    quantity: given ? given.quantity : null,
                    notes: given ? given.notes : null,
                    id: given ? given.id : null,
                    isDue,
                });
            }

            const givenCount = doses.filter((d) => d.given).length;
            const dueCount = doses.filter((d) => d.isDue && !d.given).length;
            const completionPercent = def.totalDoses > 0 ? Math.round((givenCount / def.totalDoses) * 100) : 0;

            return {
                key: def.key,
                label: def.label,
                icon: def.icon,
                category: def.category,
                totalDoses: def.totalDoses,
                notes: def.notes,
                conditional: def.conditional || false,
                hasQuantity: def.hasQuantity || false,
                hasPharmacy: def.hasPharmacy || false,
                doses,
                givenCount,
                dueCount,
                completionPercent,
                isComplete: givenCount >= def.totalDoses,
            };
        });
    }, [interventions, gestationalAge]);

    /* ─── Filtered matrix (hide/show conditional) ─── */
    const displayMatrix = useMemo(() => {
        if (showConditional) return matrixData;
        return matrixData.filter((item) => !item.conditional);
    }, [matrixData, showConditional]);

    /* ─── Summary ─── */
    const summary = useMemo(() => {
        const standard = matrixData.filter((m) => !m.conditional);
        let totalGiven = 0;
        let totalDue = 0;
        let totalRequired = 0;
        let completed = 0;

        standard.forEach((item) => {
            totalGiven += item.givenCount;
            totalDue += item.dueCount;
            totalRequired += item.totalDoses;
            if (item.isComplete) completed++;
        });

        return {
            totalGiven,
            totalDue,
            totalRequired,
            completedInterventions: completed,
            totalInterventions: standard.length,
            overallPercent: totalRequired > 0 ? Math.round((totalGiven / totalRequired) * 100) : 0,
        };
    }, [matrixData]);

    /* ─── Open record dialog ─── */
    const openRecordDialog = useCallback((interventionKey, doseNumber) => {
        const def = INTERVENTIONS.find((i) => i.key === interventionKey);
        setRecordDialog({ open: true, intervention: def, doseNumber });
        setFormData({
            date_given: new Date().toISOString().slice(0, 10),
            quantity: def && def.hasQuantity ? "30" : "",
            prescription_id: "",
            notes: "",
        });
    }, []);

    const closeRecordDialog = useCallback(() => {
        setRecordDialog({ open: false, intervention: null, doseNumber: null });
    }, []);

    /* ─── Handle form change ─── */
    const handleFormChange = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    /* ─── Submit record ─── */
    const submitRecord = useCallback(() => {
        if (!bookingData || !recordDialog.intervention) return;
        setSaving(true);

        const payload = {
            booking_id: bookingData.id,
            visit_id,
            intervention_type: recordDialog.intervention.key,
            dose_number: recordDialog.doseNumber || 1,
            date_given: formData.date_given,
            quantity: formData.quantity || null,
            // prescription_id only sent for pharmacy items (iron/folic, calcium, aspirin)
            prescription_id: formData.prescription_id ? parseInt(formData.prescription_id, 10) : null,
            notes: formData.notes || null,
        };

        axios
            .post(config.smsUrl + "/obgyn/anc/intervention", payload)
            .then((res) => {
                // Backend returns refreshed list: { booking_id, interventions: [...] }
                const data = res.data || {};
                const list = Array.isArray(data) ? data : (data.interventions || []);
                setInterventions(list);
                if (actions && actions.snackbarActions) {
                    const def = recordDialog.intervention;
                    const doseLabel = def.doseLabels
                        ? def.doseLabels[(recordDialog.doseNumber || 1) - 1] || "Dose " + recordDialog.doseNumber
                        : "Dose " + recordDialog.doseNumber;
                    actions.snackbarActions.snackSuccess(def.label + " — " + doseLabel + " recorded");
                }
                closeRecordDialog();
            })
            .catch(() => {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackError("Failed to record intervention. Please try again.");
                }
            })
            .finally(() => { setSaving(false); });
    }, [bookingData, visit_id, recordDialog, formData, actions, closeRecordDialog]);

    return {
        bookingData,
        loadingBooking,
        loadingInterventions,
        matrixData,
        displayMatrix,
        summary,
        gestationalAge,
        showConditional,
        setShowConditional,
        recordDialog,
        openRecordDialog,
        closeRecordDialog,
        formData,
        handleFormChange,
        submitRecord,
        saving,
        INTERVENTIONS,
    };
};


